import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import JSZip from 'jszip';

import { Button } from '@/components/ui/button';
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDialog } from '@/providers/DialogProvider';

interface RenderEntry {
  filename: string;
  /** Object URL backed by a Blob extracted from the zip. */
  src: string;
}

interface Props {
  layerName: string;
  /** Presigned download URL of the renders-*.zip byproduct. */
  zipUrl: string;
  /** Original byproduct filename, used for the "Download zip" link. */
  zipFilename: string;
}

/**
 * Gallery dialog showing per-surface PNG renders for a deviation layer.
 *
 * The worker bundles renders into a single ZIP byproduct rather than emitting
 * N individual PNG byproducts (cleaner DB / menu surface). On open this
 * component fetches the zip once via the presigned URL, extracts each PNG to
 * a Blob, and renders it from a per-image object URL. Object URLs are
 * revoked when the dialog unmounts so we don't leak browser memory.
 */
export function DeviationRenderGallery({ layerName, zipUrl, zipFilename }: Props) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const [entries, setEntries] = useState<RenderEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];

    (async () => {
      try {
        const res = await fetch(zipUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);

        const out: RenderEntry[] = [];
        const files = Object.values(zip.files)
          .filter((f) => !f.dir && f.name.toLowerCase().endsWith('.png'))
          .sort((a, b) => a.name.localeCompare(b.name));

        for (const file of files) {
          const blob = await file.async('blob');
          const src = URL.createObjectURL(blob);
          created.push(src);
          out.push({ filename: file.name, src });
        }

        if (!cancelled) setEntries(out);
        else created.forEach((u) => URL.revokeObjectURL(u));
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [zipUrl]);

  // Header + footer used by every state.
  const header = (
    <DialogHeader>
      <DialogTitle>{t('deviationRenders.title')}</DialogTitle>
      <DialogDescription>
        {layerName}
        {entries && (
          <span className='ml-2 text-xs text-slate-500'>
            {t('deviationRenders.count', { count: entries.length })}
          </span>
        )}
      </DialogDescription>
    </DialogHeader>
  );

  const footer = (
    <div className='mt-4 flex items-center justify-between gap-2'>
      <a
        href={zipUrl}
        download={zipFilename}
        className='text-xs text-blue-600 hover:underline dark:text-blue-400'
      >
        {t('deviationRenders.downloadZip', { defaultValue: 'Download .zip' })}
      </a>
      <Button variant='outline' onClick={() => dismissActiveDialog()}>
        {t('common.close', { defaultValue: 'Close' })}
      </Button>
    </div>
  );

  if (error) {
    return (
      <>
        {header}
        <p className='py-4 text-sm text-rose-600 dark:text-rose-400'>
          {t('deviationRenders.loadError', { defaultValue: 'Failed to load renders' })}: {error}
        </p>
        {footer}
      </>
    );
  }

  if (!entries) {
    return (
      <>
        {header}
        <div className='flex h-[60vh] items-center justify-center text-sm text-slate-500'>
          {t('deviationRenders.loading', { defaultValue: 'Loading renders…' })}
        </div>
        {footer}
      </>
    );
  }

  if (entries.length === 0) {
    return (
      <>
        {header}
        <p className='py-4 text-sm text-slate-700 dark:text-stone-200'>
          {t('deviationRenders.empty')}
        </p>
        {footer}
      </>
    );
  }

  const current = active != null ? entries[active] : null;
  const goPrev = () => setActive((i) => (i == null ? null : (i - 1 + entries.length) % entries.length));
  const goNext = () => setActive((i) => (i == null ? null : (i + 1) % entries.length));

  return (
    <>
      {header}

      {current ? (
        <div className='relative flex h-[70vh] flex-col'>
          <div className='absolute right-0 top-0 z-10 flex gap-1'>
            <a
              href={current.src}
              download={current.filename}
              className='inline-flex items-center rounded bg-slate-900/80 px-2 py-1 text-xs text-white hover:bg-slate-900'
            >
              {t('deviationRenders.download', { defaultValue: 'Download' })}
            </a>
            <button
              type='button'
              onClick={() => setActive(null)}
              className='inline-flex size-7 items-center justify-center rounded bg-slate-900/80 text-white hover:bg-slate-900'
              aria-label={t('common.close', { defaultValue: 'Close' })}
            >
              <X className='size-4' />
            </button>
          </div>
          <div className='flex flex-1 items-center justify-center'>
            <button
              type='button'
              onClick={goPrev}
              className='inline-flex size-10 items-center justify-center rounded-full bg-slate-900/60 text-white hover:bg-slate-900/80'
              aria-label='Previous'
            >
              <ChevronLeft className='size-6' />
            </button>
            <img
              src={current.src}
              alt={current.filename}
              className='mx-2 max-h-full max-w-full flex-1 object-contain'
            />
            <button
              type='button'
              onClick={goNext}
              className='inline-flex size-10 items-center justify-center rounded-full bg-slate-900/60 text-white hover:bg-slate-900/80'
              aria-label='Next'
            >
              <ChevronRight className='size-6' />
            </button>
          </div>
          <div className='mt-2 truncate text-center text-xs text-slate-500 dark:text-stone-400' title={current.filename}>
            {current.filename}
          </div>
        </div>
      ) : (
        <div className='grid max-h-[70vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3 md:grid-cols-4'>
          {entries.map((e, i) => (
            <button
              key={e.filename}
              type='button'
              onClick={() => setActive(i)}
              className='group relative aspect-square overflow-hidden rounded border border-slate-200/70 bg-slate-50 transition-colors hover:border-blue-500 dark:border-stone-700/40 dark:bg-stone-900'
            >
              <img
                src={e.src}
                alt={e.filename}
                loading='lazy'
                className='size-full object-contain transition-transform group-hover:scale-[1.02]'
              />
              <div className='absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent p-1.5 text-[10px] text-white' title={e.filename}>
                {e.filename}
              </div>
            </button>
          ))}
        </div>
      )}

      {footer}
    </>
  );
}
